import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../apiServices/axios';
import { GROUPS_PERMISSIONS } from '../../apiServices/api_routes';
import { GetGroupPermissions, UpdateGroupPermissions } from '../../redux/teamSlice';
import { VDR_PRESETS, presetFromPermissions, vdrPresetTitle } from '../NewProjectFlow/accessGroupCatalog';
import styles from './css/index.module.css';

const COLUMNS = [
    { key: 'view_perm', label: 'Preview' },
    { key: 'download_watermarked', lines: ['Download', 'watermarked'] },
    { key: 'download_original', lines: ['Download', 'original'] },
    { key: 'add_perm', label: 'Upload' },
    { key: 'delete_perm', label: 'Delete' },
];

function flagsFromPerm(perm) {
    return {
        view_perm: Boolean(perm?.view_perm),
        download_watermarked: Boolean(perm?.download_watermarked),
        download_original: Boolean(perm?.download_original),
        add_perm: Boolean(perm?.add_perm),
        delete_perm: Boolean(perm?.delete_perm),
    };
}

function ownPermission(node, presetId) {
    return (node?.permission || []).find(perm => String(perm.id) !== String(presetId)) || null;
}

function payloadFromFlags(flags, groupId, aclId, permissionId) {
    const body = {
        acl: aclId,
        group: groupId,
        ...flags,
        no_access: !flags.view_perm,
    };
    if (permissionId) body.id = permissionId;
    return body;
}

export default function AdvancedVdrPermissions({ group, onBack }) {
    const dispatch = useDispatch();
    const groupPermissions = useSelector(state => state.team.groupPermissions);
    const savingLock = useSelector(state => state.team.permissionsBtnLoading);
    const [tree, setTree] = React.useState([]);
    const [open, setOpen] = React.useState({});
    const [edits, setEdits] = React.useState({});
    const [saving, setSaving] = React.useState(false);
    const preset = VDR_PRESETS.find(item => item.key === presetFromPermissions(group?.permissions)) || VDR_PRESETS[2];
    const presetFlags = flagsFromPerm(preset.permissions);
    const presetId = group?.permissions?.id;

    React.useEffect(() => {
        if (group?.id) dispatch(GetGroupPermissions(group.id));
    }, [dispatch, group?.id]);

    React.useEffect(() => {
        if (String(groupPermissions?.id) !== String(group?.id)) return;
        const next = groupPermissions.workstreams || [];
        setTree(next);
        setOpen(prev => {
            const seeded = { ...prev };
            next.forEach(workstream => {
                if (seeded[workstream.acl_id] === undefined) seeded[workstream.acl_id] = true;
            });
            return seeded;
        });
    }, [group?.id, groupPermissions]);

    const toggleOpen = id => setOpen(prev => ({ ...prev, [id]: !prev[id] }));

    const resolve = (node, inherited) => {
        const edit = edits[node.acl_id];
        const own = ownPermission(node, presetId);
        if (edit?.mode === 'reset') return { flags: inherited, custom: false, permissionId: own?.id || null, reset: true };
        if (edit?.mode === 'override') return { flags: edit.flags, custom: true, permissionId: own?.id || null, reset: false };
        if (own) return { flags: flagsFromPerm(own), custom: true, permissionId: own.id, reset: false };
        return { flags: inherited, custom: false, permissionId: null, reset: false };
    };

    const changeFlag = (node, inherited, key) => {
        const current = resolve(node, inherited);
        const flags = { ...current.flags, [key]: !current.flags[key] };
        if (key === 'download_original' && flags.download_original) flags.download_watermarked = false;
        if (key === 'download_watermarked' && flags.download_watermarked) flags.download_original = false;
        if (flags.view_perm === false) {
            flags.add_perm = false;
            flags.delete_perm = false;
            flags.download_original = false;
            flags.download_watermarked = false;
        }
        setEdits(prev => ({ ...prev, [node.acl_id]: { mode: 'override', flags } }));
    };

    const resetRow = node => {
        setEdits(prev => ({ ...prev, [node.acl_id]: { mode: 'reset' } }));
    };

    const save = async () => {
        const update = [];
        const create = [];
        const deletions = [];
        const walk = (node, inherited) => {
            const current = resolve(node, inherited);
            const own = ownPermission(node, presetId);
            if (current.reset && own?.id) deletions.push(own.id);
            else if (edits[node.acl_id]?.mode === 'override') {
                const body = payloadFromFlags(current.flags, group.id, node.acl_id, own?.id);
                if (own?.id) update.push(body);
                else create.push(body);
            }
            (node.folders || []).forEach(folder => walk(folder, current.flags));
            (node.children || []).forEach(folder => walk(folder, current.flags));
            (node.files || []).forEach(file => walk(file, current.flags));
        };
        tree.forEach(workstream => walk(workstream, presetFlags));
        setSaving(true);
        try {
            for (const id of deletions) {
                await axiosInstance.delete(`${GROUPS_PERMISSIONS}${localStorage.project_id}/${id}/`);
            }
            if (update.length || create.length) {
                const result = await dispatch(UpdateGroupPermissions({ update, create }));
                if (!String(result?.type || '').endsWith('/fulfilled')) return;
            } else if (group?.id) {
                await dispatch(GetGroupPermissions(group.id));
            }
            setEdits({});
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (node, inherited, label, depth, parentName) => {
        const current = resolve(node, inherited);
        const expanded = Boolean(open[node.acl_id]);
        const childFolders = [...(node.folders || []), ...(node.children || [])];
        const files = node.files || [];
        const hasChildren = childFolders.length > 0 || files.length > 0;
        const inheritance = current.custom ? 'Custom' : depth === 0 ? `Inherited: ${vdrPresetTitle(preset.key)}` : `Inherited from ${parentName}`;
        const inheritanceClass = current.custom ? styles.matrixCustom : depth === 0 ? styles.matrixInherited : styles.matrixFrom;
        return (
            <React.Fragment key={`${depth}-${node.acl_id}`}>
                <tr className={depth === 0 ? styles.matrixGroup : undefined}>
                    <td className={styles.matrixResource} style={{ paddingLeft: 16 + depth * 28 }}>
                        {hasChildren ? (
                            <button type="button" className={styles.treeToggle} onClick={() => toggleOpen(node.acl_id)} aria-expanded={expanded}>
                                <span className={`${styles.caret} ${expanded ? styles.caretOpen : ''}`} />
                                <span className={depth === 0 ? styles.matrixName : undefined}>{label}</span>
                            </button>
                        ) : (
                            <span className={styles.treeLeaf}>{label}</span>
                        )}
                    </td>
                    <td className={inheritanceClass}>{inheritance}</td>
                    {COLUMNS.map(column => (
                        <td key={column.key}>
                            <button
                                type="button"
                                className={styles.matrixBoxBtn}
                                aria-label={column.label || column.lines.join(' ')}
                                aria-pressed={current.flags[column.key]}
                                onClick={() => changeFlag(node, inherited, column.key)}>
                                <span className={`${styles.matrixBox} ${current.flags[column.key] ? styles.matrixBoxOn : ''}`}>
                                    {current.flags[column.key] ? <span className={styles.matrixTick} /> : null}
                                </span>
                            </button>
                        </td>
                    ))}
                    <td className={styles.matrixResetCell}>
                        <button type="button" className={styles.matrixReset} onClick={() => resetRow(node)}>
                            Reset
                        </button>
                    </td>
                </tr>
                {expanded ? childFolders.map(folder => renderRow(folder, current.flags, folder.name, depth + 1, label)) : null}
                {expanded ? files.map(file => renderRow(file, current.flags, file.name, depth + 1, label)) : null}
            </React.Fragment>
        );
    };

    return (
        <div className={styles.permPage}>
            <p className={styles.crumb}>
                <button type="button" onClick={onBack}>
                    Project access
                </button>
                <span className={styles.crumbSep}>›</span>
                <button type="button" onClick={onBack}>
                    {group?.name}
                </button>
                <span className={styles.crumbSep}>›</span>
                <span>Advanced VDR permissions</span>
            </p>
            <h2 className={styles.title}>Advanced VDR permissions</h2>
            <p className={styles.subtitle}>Override the group's default VDR access for specific workstreams, folders or files.</p>
            <div className={styles.matrixWrap}>
                <table className={styles.matrix}>
                    <thead>
                        <tr>
                            <th>Resource</th>
                            <th>Inheritance</th>
                            {COLUMNS.map(column => (
                                <th key={column.key}>
                                    {column.lines ? (
                                        <span className={styles.matrixHeadStack}>
                                            {column.lines.map(line => (
                                                <span key={line}>{line}</span>
                                            ))}
                                        </span>
                                    ) : (
                                        column.label
                                    )}
                                </th>
                            ))}
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {tree.length === 0 ? (
                            <tr>
                                <td className={styles.matrixEmpty} colSpan={8}>
                                    No workstreams to configure.
                                </td>
                            </tr>
                        ) : (
                            tree.map(workstream => renderRow(workstream, presetFlags, workstream.name, 0, workstream.name))
                        )}
                    </tbody>
                </table>
            </div>
            <div className={styles.footer}>
                <button type="button" className={styles.primaryBtn} disabled={saving || savingLock} onClick={save}>
                    {saving || savingLock ? 'Saving…' : 'Save VDR permissions'}
                </button>
                <button type="button" className={styles.textBtn} onClick={onBack}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
